import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSandboxPreview } from "@/contexts/SandboxPreviewContext";

type FlagRow = {
  key: string;
  status: "sandbox" | "live" | "rolled_back";
  enabled_for_roles: string[];
  enabled_for_plans: string[];
};

const cache = new Map<string, FlagRow | null>();

export function useFeatureFlag(flagKey: string) {
  const { preview } = useSandboxPreview();
  const [flag, setFlag] = useState<FlagRow | null | undefined>(cache.get(flagKey));

  useEffect(() => {
    if (cache.has(flagKey)) {
      setFlag(cache.get(flagKey));
      return;
    }
    let mounted = true;
    supabase
      .rpc("get_feature_flag", { p_key: flagKey })
      .maybeSingle()
      .then(({ data }) => {
        const row = (data as FlagRow) ?? null;
        cache.set(flagKey, row);
        if (mounted) setFlag(row);
      });
    return () => {
      mounted = false;
    };
  }, [flagKey]);

  const isSandboxPreview =
    preview?.feature_key === flagKey ||
    (preview?.feature_key === "*" && flag?.status === "sandbox");
  const liveEnabled = flag?.status === "live";

  return {
    enabled: liveEnabled || isSandboxPreview,
    isSandboxPreview,
    loading: flag === undefined,
  };
}

export function clearFeatureFlagCache() {
  cache.clear();
}
